import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { format } from 'date-fns';
import {
  changeAccountingMonth,
  dashboardClosedMonthsAccountingMonthSelector,
  dashboardClosedMonthsClosedMonthsSelector,
  findClosedMonthsRequested,
} from '../../../../state';
import ClosingContent from './ClosingContent';
import { useMuiPickersLocale } from '../../../../hooks';
import DashboardWidgetCard from '../../DashboardWidgetCard';

const lastMonth = new Date();
lastMonth.setDate(0);
lastMonth.setDate(1);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

interface ButtonProps {
  onClick: () => void;
  disabled: boolean;
}

const PreviousButton = ({ onClick, disabled }: ButtonProps) => (
  <Button onClick={onClick} disabled={disabled}>
    <NavigateBeforeIcon />
  </Button>
);
const NextButton = ({ onClick, disabled }: ButtonProps) => (
  <Button onClick={onClick} disabled={disabled}>
    <NavigateNextIcon />
  </Button>
);

const MonthClosingIncorporation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const accountingMonth = useSelector(dashboardClosedMonthsAccountingMonthSelector);
  const closedMonths = useSelector(dashboardClosedMonthsClosedMonthsSelector);
  const locale = useMuiPickersLocale();

  const handleChangeAccountingMonthPrevious = useCallback(() => {
    accountingMonth.setDate(0);
    dispatch(changeAccountingMonth(accountingMonth));
    dispatch(findClosedMonthsRequested());
  }, [accountingMonth, dispatch]);

  const handleChangeAccountingMonthNext = useCallback(() => {
    const nextMonthLastDay = new Date(
      accountingMonth.getFullYear(),
      accountingMonth.getMonth() + 2,
      0
    );
    dispatch(changeAccountingMonth(nextMonthLastDay));
    dispatch(findClosedMonthsRequested());
  }, [accountingMonth, dispatch]);

  return (
    <DashboardWidgetCard
      title={`${t('dashboard.month-closing-status')} ${format(accountingMonth, 'MMMM yyyy', {
        locale,
      })}`}
      actions={
        <Container>
          <PreviousButton
            onClick={handleChangeAccountingMonthPrevious}
            disabled={accountingMonth <= new Date('2022-02-01')}
          />
          <NextButton
            onClick={handleChangeAccountingMonthNext}
            disabled={accountingMonth >= lastMonth}
          />
        </Container>
      }
    >
      <ClosingContent resource={closedMonths} accountingMonth={accountingMonth} />
    </DashboardWidgetCard>
  );
};

export default MonthClosingIncorporation;
